import './styles.scss';
import React, { useMemo, useContext } from 'react';
import { Container } from '@edx/paragon';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import breakpoints from '@edx/paragon/dist/utils/breakpoints';

import ProductCardWrapper from '@prospectus/common/ui/ProductCard/Wrapper';
import ClassicEdxCoursePropTypes from '@prospectus/common/types/classicEdxCoursePropTypes';
import ClassicEdxProgramPropTypes from '@prospectus/common/types/classicEdxProgramPropTypes';
import MarketingTakeover from '@prospectus/common/ui/MarketingTakeover';
import ProductPropTypes from '@prospectus/common/types/productPropTypes';
import { useWindowSize } from '@utils/responsive';
import { WebviewContext } from '@utils/context';
import { addOptimizelyTracking } from '@utils/analytics';
import { marketingBannerPropTypes } from '@prospectus/common/ui/MarketingTakeover/propTypes';
import { MAX_CARDS_TO_DISPLAY_BEFORE_EXPANDED } from '../ExpandableCardList/constants';

export const MAX_CARDS_TO_DISPLAY_MOBILE = 4;

function CardContainer({
  productList,
  marketingBanner,
  isScrollable,
  isExpandable,
  cardsPerRow,
  dynamicProductList,
  shouldWrap,
  additionalClassNames,
  linkCard,
  cardsPerPage,
  shouldfirePreQuerySegmentEvent,
  searchProps: {
    isLoading, analytics, urlParams,
  },
  useCohesion,
}) {
  const isWebview = useContext(WebviewContext);

  // Move this to a utility function if more than one container becomes expandable
  const getCardVisibility = (index) => {
    if (index < MAX_CARDS_TO_DISPLAY_BEFORE_EXPANDED) {
      return 'visible';
    }
    return 'hidden';
  };

  const size = useWindowSize();

  const CARDS_PER_PAGE = cardsPerPage || 32;
  const DEFAULT_BANNER_POSITION = 5;
  const shouldShowLinkCard = isScrollable
    && size.width <= breakpoints.extraLarge.minWidth
    && productList.length > 0 && !!linkCard;

  const finalProductList = useMemo(() => {
    if (isScrollable && productList && !dynamicProductList) {
      return productList.slice(0, MAX_CARDS_TO_DISPLAY_MOBILE);
    }
    return productList;
  }, [productList, isScrollable]);

  const bannerPosition = marketingBanner?.showBannerAtBottom ? Math.floor(CARDS_PER_PAGE / 2) : DEFAULT_BANNER_POSITION;

  return (
    <>
      <div
        className={classNames(
          'card-container d-flex',
          { 'flex-wrap': !isScrollable && shouldWrap && !dynamicProductList },
          { 'three-cards': cardsPerRow === 3 },
          { 'flex-nowrap scrollable': isScrollable && !dynamicProductList },
          {
            'flex-nowrap flex-lg-wrap scrollable':
              isScrollable && dynamicProductList,
          },
          additionalClassNames,
        )}
        data-testid="card-container"
      >
        {finalProductList
          && finalProductList.map((item, index) => {
            // if the list is not expandable all cards will be visible
            const cardVisibility = isExpandable
              ? getCardVisibility(index)
              : 'visible';
            const finalAnalytics = addOptimizelyTracking(item, analytics);
            const finalUrlParams = addOptimizelyTracking(item, urlParams);

            return (
              <React.Fragment key={item.uuid}>
                <ProductCardWrapper
                  shouldfirePreQuerySegmentEvent={shouldfirePreQuerySegmentEvent}
                  key={item.uuid}
                  product={item}
                  urlParams={finalUrlParams}
                  isLoading={isLoading}
                  analytics={{
                    ...finalAnalytics,
                    card_visibility: cardVisibility,
                  }}
                  webview={isWebview}
                  useCohesion={useCohesion}
                />
                {marketingBanner && (index + 1 === bannerPosition) && (
                  <Container size="lg" className={classNames('p-0 m-0 pr-4', { 'hidden-on-small': marketingBanner.showBannerAtBottom })}>
                    {marketingBanner && (
                      <MarketingTakeover {...marketingBanner} />
                    )}
                  </Container>
                )}
              </React.Fragment>
            );
          })}
        {marketingBanner && finalProductList
          && finalProductList.length > 0
          && finalProductList.length < bannerPosition && (
            <Container size="lg" className={classNames('hidden-on-small')}>
              <MarketingTakeover {...marketingBanner} />
            </Container>
        )}
        {shouldShowLinkCard && linkCard}
      </div>
      {marketingBanner?.showBannerAtBottom && finalProductList
          && finalProductList.length > 0 && (
            <Container size="lg" className="mt-3 hidden-on-large">
              <MarketingTakeover {...marketingBanner} />
            </Container>
      )}
    </>
  );
}

CardContainer.propTypes = {
  shouldfirePreQuerySegmentEvent: PropTypes.bool,
  productList: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape(ClassicEdxCoursePropTypes.course),
      PropTypes.shape(ClassicEdxProgramPropTypes.program),
      PropTypes.shape(ProductPropTypes),
    ]),
  ).isRequired,
  marketingBanner: PropTypes.shape({
    ...marketingBannerPropTypes,
    showBannerAtBottom: PropTypes.bool,
  }),
  isScrollable: PropTypes.bool,
  isExpandable: PropTypes.bool,
  dynamicProductList: PropTypes.bool,
  shouldWrap: PropTypes.bool,
  additionalClassNames: PropTypes.string,
  cardsPerRow: PropTypes.number,
  linkCard: PropTypes.element,
  searchProps: PropTypes.shape({
    isLoading: PropTypes.bool,
    analytics: PropTypes.shape({
      index: PropTypes.string,
      results_level: PropTypes.string,
    }),
    urlParams: PropTypes.shape({
      results_level: PropTypes.string,
    }),
    hasDeviceParam: PropTypes.bool,
  }),
  cardsPerPage: PropTypes.number,
  useCohesion: PropTypes.bool,
};

CardContainer.defaultProps = {
  shouldfirePreQuerySegmentEvent: false,
  marketingBanner: null,
  isScrollable: false,
  isExpandable: false,
  shouldWrap: true,
  additionalClassNames: '',
  linkCard: undefined,
  dynamicProductList: false,
  cardsPerRow: 4,
  searchProps: {
    isLoading: false,
    analytics: undefined,
    urlParams: undefined,
    hasDeviceParam: false,
  },
  cardsPerPage: undefined,
  useCohesion: false,
};

export default CardContainer;
